import { gql } from '@apollo/client'
import { STATUS_DELIVERED, AGG_STATUS_DELIVERED, AGG_STATUS_CHECKOUT, ORDER_STATUS } from '../types/Order'

export type BaseData = {
  ordersTotal: number

  ordersQuantity: number
  ordersByPharmaciesAverage: number
  ordersAverage: number

  activationsQuantity: number
  pharmaciesQuantity: number

  customersByDayAverage: number
  customersQuantity: number
}

export type BaseAccomplishedData = {
  ordersTotal: number

  ordersQuantity: number
  ordersByPharmaciesAverage: number
  ordersAverage: number

  activationsQuantity: number
  pharmaciesQuantity: number
}

export type GoalPartial = {
  ordersTotal: number
  ordersQuantity: number
  activationsQuantity: number
}

export interface AccomplishedData {
  year: number
  month: number

  yearBefore: BaseAccomplishedData
  projection: BaseAccomplishedData
  yearVariation: BaseAccomplishedData
}

export interface DailyMonitoring {
  goalMonth: BaseData,
  goalPartial: GoalPartial
  fulfilledInMonth: BaseData,
  goalDiff: BaseData,
  goalDiffPercent: BaseData,
  accomplishedData: AccomplishedData
}

export const GET_SUMARY_DATA = gql`
  query DailyMonitoring($filters: DailyMonitoringInput!) {
    dailyMonitoring(filters: $filters) {
      goalMonth {
        ordersTotal
        ordersQuantity
        ordersByPharmaciesAverage
        ordersAverage
        activationsQuantity
        pharmaciesQuantity
        customersByDayAverage
        customersQuantity
      }
      goalPartial {
        ordersTotal
        ordersQuantity
        activationsQuantity
      }
      fulfilledInMonth {
        ordersTotal
        ordersQuantity
        ordersByPharmaciesAverage
        ordersAverage
        activationsQuantity
        pharmaciesQuantity
        customersByDayAverage
        customersQuantity
      }
      goalDiff {
        ordersTotal
        ordersQuantity
        ordersByPharmaciesAverage
        ordersAverage
        activationsQuantity
        pharmaciesQuantity
        customersByDayAverage
        customersQuantity
      }
      goalDiffPercent {
        ordersTotal
        ordersQuantity
        ordersByPharmaciesAverage
        ordersAverage
        activationsQuantity
        pharmaciesQuantity
        customersByDayAverage
        customersQuantity
      }
      accomplishedData {
        year
        month
        yearBefore {
          ordersTotal
          ordersQuantity
          ordersByPharmaciesAverage
          ordersAverage
          activationsQuantity
          pharmaciesQuantity
        }
        projection{
          ordersTotal
          ordersQuantity
          ordersByPharmaciesAverage
          ordersAverage
          activationsQuantity
          pharmaciesQuantity
        }
        yearVariation {
          ordersTotal
          ordersQuantity
          ordersByPharmaciesAverage
          ordersAverage
          activationsQuantity
          pharmaciesQuantity
        }
      }
    }
  }
`

export const GET_ORDERS_TOTAL_HISTORY = gql`
  query OrdersHistory($filters: JSON) {
    ordersAggs(
      filters: $filters,
      size: 0,
      aggs: {
        salesOverTime: {
          date_histogram: {
            field: "created_at",
            calendar_interval: "day",
            time_zone: "-03:00"
          },
          aggs: {
            ${AGG_STATUS_DELIVERED}: {
              filter: {
                term: {
                  status_translate_keyword: "${STATUS_DELIVERED}"
                }
              },
              aggs: {
                total: {
                  sum: {
                    field: "total"
                  }
                }
              }
            }
          }
        }
      }
    ) {
      aggregations
    }
  }
`

export const GET_ORDERS_QUANTITY_HISTORY = gql`
  query OrdersQuantityHistory($filters: JSON) {
    ordersAggs(
      filters: $filters,
      size: 0,
      aggs: {
        salesOverTime: {
          date_histogram: {
            field: "created_at",
            calendar_interval: "day",
            time_zone: "-03:00"
          },
          aggs: {
            ${AGG_STATUS_DELIVERED}: {
              filter: {
                term: {
                  new_status: "${ORDER_STATUS.DELIVERED}"
                }
              },
              aggs: {
                quantity: {
                  value_count: {
                    field: "_id"
                  }
                }
              }
            }
          }
        }
      }
    ) {
      aggregations
    }
  }
`

export const GET_ORDERS_OPEN_GMV_QUANTITY_HISTORY = gql`
  query OrdersQuantityHistory($filters: JSON) {
    ordersAggs(
      filters: $filters,
      size: 0,
      aggs: {
        salesOverTime: {
          date_histogram: {
            field: "created_at",
            calendar_interval: "day",
            time_zone: "-03:00"
          },
          aggs: {
            ${AGG_STATUS_CHECKOUT}: {
              filter: {
                terms: {
                  new_status: ["${ORDER_STATUS.WAITING_CONFIRMATION}", "${ORDER_STATUS.AVAILABLE}", "${ORDER_STATUS.CONFIRMED}", "${ORDER_STATUS.DELIVERING}"]
                }
              },
              aggs: {
                general: {
                  sum: {
                    field: "total"
                  }
                },
                inSla: {
                  filter: {
                    script: {
                      script: """
                        return new Date().getTime() <= doc['shipping.expectedShippingDate'].value.getMillis();
                      """
                    }
                  },
                  aggs: {
                    total: {
                      sum: {
                        field: "total"
                      }
                    }
                  }
                },
                outSla: {
                  filter: {
                    script: {
                      script: """
                        return new Date().getTime() > doc['shipping.expectedShippingDate'].value.getMillis();
                      """
                    }
                  },
                  aggs: {
                    total: {
                      sum: {
                        field: "total"
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    ) {
      aggregations
    }
  }
`

export const GET_ORDERS_TOTAL_AVERAGE_HISTORY = gql`
  query OrdersTotalAverageyHistory($filters: JSON) {
    ordersAggs(
      filters: $filters,
      size: 0,
      aggs: {
        salesOverTime: {
          date_histogram: {
            field: "created_at",
            calendar_interval: "day",
            time_zone: "-03:00"
          },
          aggs: {
            ${AGG_STATUS_DELIVERED}: {
              filter: {
                term: {
                  status_translate_keyword: "${STATUS_DELIVERED}"
                }
              },
              aggs: {
                total: {
                  avg: {
                    field: "total"
                  }
                }
              }
            }
          }
        }
      }
    ) {
      aggregations
    }
  }
`
export const GET_ACTIVATION = gql`
  query ActivationQuantity($filters: JSON) {
    aggregation(
      index: "pharmacy-global-configuration",
      size: 0,
      filters: $filters,  
      aggs: {
        activations: {
          date_histogram: {
            field: "activatedAt",
            calendar_interval: "day",
            time_zone: "-03:00"
          }
        }
      }
    ) {
      aggregations
    }
  }
`
export const GET_APPLICATION_GROUPDATA = gql`
  query getAppsGroupedData($year: Int!, $month: Int!) {
    getAppsGroupedData(year: $year, month: $month) {
      platform
      totalInstalls
      totalUninstalls
    }
  }
`
export const GET_CREDENTIALS_QUANTITY = gql`
  query CredentialsQuantity($filters: JSON) {
    aggs(
      index: "pharmacy-global-configuration",
      size: 0,
      filters: $filters,
      aggs: {
        credentials: {
          date_histogram: {
            field: "createdAt",
            calendar_interval: "day",
            time_zone: "-03:00"
          }
        }
      }
    ) {
      aggregations
    }
  }
`
export const GET_APPLICATION_VALUE = gql`
  query DailyMonitoringApplication($year: Int!, $month: Int!) {
    getAppsData(year: $year, month: $month) {
      platform
      eventTime
      installs
      uninstalls
    }
  }
`

export const GET_PHARMACIES_QUANTITY_HISTORY = gql`
  query PhamaciesQuantityHistory($filters: JSON) {
    ordersAggs(
      filters: $filters,
      size: 0,
      aggs: {
        salesOverTime: {
          date_histogram: {
            field: "created_at",
            calendar_interval: "day",
            time_zone: "-03:00"
          },
          aggs: {
            ${AGG_STATUS_DELIVERED}: {
              filter: {
                term: {
                  status_translate_keyword: "${STATUS_DELIVERED}"
                }
              },
              aggs: {
                quantity: {
                  cardinality: {
                    field: "pharmacy_id"
                  }
                }
              }
            }
          }
        }
      }
    ) {
      aggregations
    }
  }
`

export const GET_CUSTOMERS_QUANTITY_HISTORY = gql`
  query CustomersQuantityHistory($filters: JSON) {
    ordersAggs(
      filters: $filters,
      size: 0,
      aggs: {
        salesOverTime: {
          date_histogram: {
            field: "created_at",
            calendar_interval: "day",
            time_zone: "-03:00"
          },
          aggs: {
            ${AGG_STATUS_DELIVERED}: {
              filter: {
                term: {
                  status_translate_keyword: "${STATUS_DELIVERED}"
                }
              },
              aggs: {
                quantity: {
                  cardinality: {
                    field: "customer_email"
                  }
                }
              }
            }
          }
        }
      }
    ) {
      aggregations
    }
  }
`

export const GET_ORDERS_PHARMACIES_QUANTITY_HISTORY = gql`
  query OrdersPharmaciesQuantityHistory($filters: JSON) {
    ordersAggs(
      filters: $filters,
      size: 0,
      sort: [{
        created_at: {
          order: "asc"
        }
      }],
      query: {
        bool: {
          filter: [{
            term: {
              status_translate_keyword: "${STATUS_DELIVERED}"
            }
          }]
        }
      },
      aggs: {
        salesOverTime: {
          date_histogram: {
            field: "created_at",
            calendar_interval: "day",
            time_zone: "-03:00"
          },
          aggs: {
            pharmaciesQuantity: {
              cardinality: {
                field: "pharmacy_id"
              }
            },
            ordersQuantity: {
              value_count: {
                field: "_id"
              }
            }
          }
        }
      }
    ) {
      aggregations
    }
  }
`
